import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkle, Info, CaretDown, ArrowClockwise, Trash, ShieldCheck, Warning, EyeClosed, ArrowsClockwise, Lightning, GitMerge, Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const TZ = [
  { label: "India (IST, +5:30)", v: 5.5 }, { label: "UTC (+0)", v: 0 }, { label: "Central Europe (+1)", v: 1 },
  { label: "Gulf (+4)", v: 4 }, { label: "Pakistan (+5)", v: 5 }, { label: "Bangladesh (+6)", v: 6 },
  { label: "Singapore/China (+8)", v: 8 }, { label: "Japan (+9)", v: 9 }, { label: "Australia East (+10)", v: 10 },
  { label: "US Eastern (-5)", v: -5 }, { label: "US Central (-6)", v: -6 }, { label: "US Mountain (-7)", v: -7 }, { label: "US Pacific (-8)", v: -8 },
];

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span><div className="mt-1.5">{children}</div></label>;
}
const inputCls = "w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30";

export default function Symbolic() {
  const [data, setData] = useState(undefined);
  const [form, setForm] = useState({ name: "", dob: "", tob: "", tz_offset: 5.5, birth_place: "", lat: "", lon: "" });
  const [adv, setAdv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { api.get("/symbolic").then((r) => setData(r.data.symbolic)).catch(() => setData(null)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.dob) { toast.error("Name and date of birth are required."); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, dob: form.dob, tob: form.tob || null, tz_offset: Number(form.tz_offset),
        birth_place: form.birth_place || null, lat: form.lat ? Number(form.lat) : null, lon: form.lon ? Number(form.lon) : null };
      const { data } = await api.post("/symbolic", payload);
      setData(data.symbolic);
      toast.success("Your Pattern Mirror is ready.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not compute your mirror."); }
    finally { setSaving(false); }
  };

  const refresh = async () => { setRefreshing(true); try { const { data } = await api.post("/symbolic/refresh"); setData(data.symbolic); toast.success("Refreshed with your latest assessments."); } finally { setRefreshing(false); } };
  const remove = async () => { await api.delete("/symbolic"); setData(null); toast.success("Birth data removed."); };

  if (data === undefined) return <div className="flex justify-center py-32"><div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-800 border-t-indigo-400" /></div>;

  return (
    <div data-testid="symbolic-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Pattern Mirror · Optional</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-4xl">Your Pattern Mirror</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">A reflective lens on the strengths, blind spots, and growth edges associated with your details and assessments.</p>

      <div className="mt-6 flex items-start gap-2.5 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4" data-testid="symbolic-disclaimer">
        <Info size={16} weight="duotone" className="mt-0.5 shrink-0 text-indigo-300" />
        <p className="text-xs leading-relaxed text-slate-400">This is a <span className="text-indigo-300">reflective tool, not a prediction or diagnosis</span>. It helps you notice patterns and make practical changes. Everything below is a <span className="text-slate-200">tendency</span>, never a certainty.</p>
      </div>

      {!data ? (
        <form onSubmit={submit} className="mt-6 max-w-xl rounded-2xl border border-slate-800 bg-slate-900 p-6 space-y-4" data-testid="symbolic-form">
          <Field label="Full name (as given at birth)"><input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Avnni Sharma" className={inputCls} data-testid="sym-name" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date of birth"><input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls} data-testid="sym-dob" /></Field>
            <Field label="Time of birth (optional)"><input type="time" value={form.tob} onChange={(e) => set("tob", e.target.value)} className={inputCls} data-testid="sym-tob" /></Field>
          </div>
          <Field label="Birth timezone"><select value={form.tz_offset} onChange={(e) => set("tz_offset", e.target.value)} className={inputCls} data-testid="sym-tz">{TZ.map((t, i) => <option key={i} value={t.v}>{t.label}</option>)}</select></Field>
          <Field label="Birth place (optional)"><input value={form.birth_place} onChange={(e) => set("birth_place", e.target.value)} placeholder="City, country" className={inputCls} data-testid="sym-place" /></Field>
          <button type="button" onClick={() => setAdv((v) => !v)} className="flex items-center gap-1.5 text-xs font-medium text-indigo-300"><CaretDown size={13} className={adv ? "rotate-180" : ""} /> Advanced — add coordinates for a finer read</button>
          {adv && (<div className="grid gap-4 sm:grid-cols-2">
            <Field label="Latitude"><input value={form.lat} onChange={(e) => set("lat", e.target.value)} placeholder="e.g. 28.61" className={inputCls} data-testid="sym-lat" /></Field>
            <Field label="Longitude"><input value={form.lon} onChange={(e) => set("lon", e.target.value)} placeholder="e.g. 77.20" className={inputCls} data-testid="sym-lon" /></Field>
          </div>)}
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-indigo-400 disabled:opacity-50" data-testid="sym-submit">
            {saving ? <><ArrowClockwise size={15} className="animate-spin" /> Preparing your reflective insights…</> : <><Sparkle size={15} weight="fill" /> Reveal my Pattern Mirror</>}
          </button>
        </form>
      ) : (
        <Mirror record={data} onRefresh={refresh} onRemove={remove} refreshing={refreshing} />
      )}
    </div>
  );
}

const ACCENTS = {
  indigo: { icon: "text-indigo-300", dot: "bg-indigo-400" },
  emerald: { icon: "text-emerald-300", dot: "bg-emerald-400" },
  amber: { icon: "text-amber-300", dot: "bg-amber-400" },
  sky: { icon: "text-sky-300", dot: "bg-sky-400" },
};
const ListCard = ({ icon: Icon, title, items, accent = "indigo", testid }) => {
  const a = ACCENTS[accent] || ACCENTS.indigo;
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid={testid}>
      <div className="flex items-center gap-2"><Icon size={16} weight="duotone" className={a.icon} /><h3 className="font-heading text-base font-medium text-slate-100">{title}</h3></div>
      <ul className="mt-3 space-y-2">{(items || []).map((s, i) => <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-slate-300"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${a.dot}`} /> {s}</li>)}</ul>
    </div>
  );
};

function Mirror({ record, onRefresh, onRemove, refreshing }) {
  const m = record.mirror || {};
  const align = m.assessment_alignment || [];
  return (
    <div className="mt-6 space-y-5" data-testid="symbolic-result">
      {/* Summary */}
      <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-7" data-testid="mirror-summary">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Top-level summary</p>
        <p className="mt-3 text-base leading-relaxed text-slate-200">{m.summary}</p>
        {m.stress_response && <p className="mt-4 text-sm text-slate-400"><span className="text-indigo-300">Under pressure: </span>{m.stress_response}</p>}
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <ListCard icon={ShieldCheck} title="Core strengths" items={m.strengths} accent="emerald" testid="mirror-strengths" />
        <ListCard icon={Warning} title="Growth edges & blind spots" items={m.growth_edges} accent="amber" testid="mirror-growth" />
        <ListCard icon={EyeClosed} title="Hidden beliefs" items={m.hidden_beliefs} accent="indigo" testid="mirror-beliefs" />
        <ListCard icon={ArrowsClockwise} title="Recurring patterns" items={m.recurring_patterns} accent="sky" testid="mirror-patterns" />
      </div>

      {/* Growth invitation */}
      {m.growth_invitation && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6" data-testid="mirror-invitation">
          <div className="flex items-center gap-2"><Lightbulb size={16} weight="duotone" className="text-amber-400" /><h3 className="font-heading text-base font-medium text-slate-100">Your growth invitation</h3></div>
          <p className="mt-2 text-sm leading-relaxed text-slate-200">{m.growth_invitation}</p>
        </div>
      )}

      {/* Immediate actions */}
      <div className="rounded-2xl border border-teal-500/30 bg-slate-900 p-6" data-testid="mirror-actions">
        <div className="flex items-center gap-2"><Lightning size={16} weight="duotone" className="text-teal-400" /><h3 className="font-heading text-base font-medium text-slate-100">Start with one small change this week</h3></div>
        <ul className="mt-3 space-y-2">{(m.immediate_actions || []).map((a, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-0.5 font-mono text-xs text-teal-400">{i + 1}</span> {a}</li>)}</ul>
      </div>

      {/* Assessment alignment */}
      {align.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="mirror-alignment">
          <div className="flex items-center gap-2"><GitMerge size={16} weight="duotone" className="text-indigo-300" /><h3 className="font-heading text-base font-medium text-slate-100">How this aligns with your assessments</h3></div>
          <div className="mt-4 space-y-3">
            {align.map((a, i) => (
              <div key={i} className="rounded-xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-200">{a.theme}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs ${a.type === "tension" ? "bg-amber-500/15 text-amber-300" : "bg-teal-500/15 text-teal-300"}`}>{a.type === "tension" ? "Tension" : "Match"}</span>
                </div>
                <p className="mt-1.5 text-sm text-slate-400">{a.why}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button onClick={onRefresh} disabled={refreshing} className="inline-flex items-center gap-2 rounded-full border border-indigo-500/40 px-5 py-2.5 text-sm font-medium text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-50" data-testid="mirror-refresh"><ArrowClockwise size={15} className={refreshing ? "animate-spin" : ""} /> Refresh with my latest assessments</button>
        <button onClick={onRemove} className="inline-flex items-center gap-2 rounded-full border border-red-500/30 px-5 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10" data-testid="symbolic-delete"><Trash size={15} /> Remove my birth data</button>
      </div>
    </div>
  );
}
